import { Dialog } from '@/components/shared/components/Dialog';
import { Button } from 'primereact/button';
import { ReactNode, useState } from 'react';
import { DialogBody, ErrorText, FormActions } from './TemplateFormDialog.StyledComponents';

interface ConfirmDialogProps {
    visible: boolean;
    title: string;
    message: ReactNode;
    confirmLabel: string;
    confirmSeverity?: 'danger' | 'success';
    onConfirm: () => Promise<unknown>;
    onCancel: () => void;
}

const errorMessageFrom = (error: unknown): string =>
    error instanceof Error ? error.message : 'Something went wrong. Please try again.';

export const ConfirmDialog = ({
    visible,
    title,
    message,
    confirmLabel,
    confirmSeverity,
    onConfirm,
    onCancel,
}: ConfirmDialogProps) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleConfirm = async () => {
        setIsSubmitting(true);
        setError(null);
        try {
            await onConfirm();
        } catch (err) {
            setError(errorMessageFrom(err));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog
            visible={visible}
            onHide={onCancel}
            header={title}
            data-testid="confirm-dialog"
            appendTo="self"
        >
            <DialogBody>
                <div>{message}</div>
                {error ? <ErrorText data-testid="confirm-dialog-error">{error}</ErrorText> : null}
                <FormActions>
                    <Button
                        label="Cancel"
                        text
                        type="button"
                        data-testid="confirm-dialog-cancel"
                        onClick={onCancel}
                    />
                    <Button
                        label={confirmLabel}
                        type="button"
                        severity={confirmSeverity}
                        data-testid="confirm-dialog-confirm"
                        disabled={isSubmitting}
                        onClick={handleConfirm}
                    />
                </FormActions>
            </DialogBody>
        </Dialog>
    );
};
